import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import DocumentTitle from 'react-document-title';
import { Link } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import htmlDocx from 'html-docx-js/dist/html-docx';
import FileSaver from 'file-saver';
import { withRouter } from 'react-router';
import { withTranslation } from 'react-i18next';

import { makeGetCurrentUserId, makeGetResolvedDocUsers } from '../selectors/users';
import { makeGetNotificationsByAssociationId } from '../selectors/notifications';
import { makeGetDoc, makeGetDocPages } from '../selectors/docs';
import { makeGetExternalAccountByProvider } from '../selectors/external-accounts';
import { makeGetLoadingState, makeGetFetchedState } from '../selectors/global';

import { fetchDoc, fetchDocs, updateDoc, createDoc, removeDoc } from '../actions/docs';
import { generatePDF } from '../api/docs-api';

import { getCleanIdFromUrl, delegateHover, copyUrlToClipboard, openWindow } from '../utils';
import { getDocLastUpdate, getDocPermission } from '../utils/doc';
import { getToolbarMenuItems, getDocPrintHtml } from '../utils/doc-editor';
import { buildAppUrl } from '../utils/url';
import { isElectron } from 'services/env-service';
import { createAlert } from '../services/alert-service';
import { trackEvent, startTrackSession, stopTrackSession } from '../services/events-tracker-service';
import eventBus from '../services/event-bus';
import { getAccountToken } from '../api/external-accounts-api';

import AppLoading from './app-loading';
import AvatarBase from './avatar-base';
import DropdownBtn from './dropdown-btn';
import CanUser from './can-user';
import ChatToggle from './chat-toggle';
import DocToolbar from './doc-toolbar';
import DocMenu from './doc-menu';
import GoogleDoc from './google-doc';
import OfficeDoc from './office-doc';
import DocumentUsers from './document-users';
import Tooltip from './tooltip';
import DocActions from 'components/DocActions';
import DocPagesList from 'components/DocPagesList';
import NiftyDoc from 'components/NiftyDoc';
import ProjectAvatar from 'components/ProjectAvatar';
import NotificationsReader from 'components/NotificationsReader';

import IconShare from '../icons/icon-share';
import IconArrowsLeft from '../icons/icon-arrows-left';

import Placeholder from './placeholders/document.placeholder';

import { SocketContext } from '../context/SocketContext';
import { ThemeContext } from '../context/ThemeContext';

class Document extends PureComponent {
    static contextType = SocketContext;

    constructor(props) {
        super(props);

        this.state = {
            isCollapsed: true,
            scrollPosition: 0,
            currentPage: 'head'
        };

        this.handleWindowUnload = this.handleWindowUnload.bind(this);
    }

    handleNameChange = name => {
        this.setState({ name });
    }

    handleWindowUnload() {
        this.closeComponent();
    }

    handleMenuAction = type => {
        switch (type) {
            case 'undo':
                this.docCtrl && this.docCtrl.undo();
                break;

            case 'redo':
                this.docCtrl && this.docCtrl.redo();
                break;

            default: 
                this.docCtrl && this.docCtrl.toggleType(type);
        }
    }

    handleScroll = event => {
        const element = event?.target;

        if (!element) {
            this.setState({
                scrollPosition: 0
            });

            this.docCtrl && this.docCtrl.updateScrollPosition(0);

            return;
        }

        this.setState({
            scrollPosition: element.scrollTop / (element.scrollHeight - element.clientHeight) * 100
        });

        this.docCtrl && this.docCtrl.updateScrollPosition(element.scrollTop);
    }

    handleShare = () => {
        this.shareCtrl.hide();
    }

    handleBackClick = () => {
        this.goBack();
    }

    goBack() {
        this.props.history.push(`/${this.props.projectId}/documents/${this.props.match.params.folderPath || ''}`);
    }

    getDocPrintHtml = async (singlePage, withStyles = true) => {
        const { doc, pages } = this.props;

        let otherPages = [];

        if (!singlePage && pages?.length) {
            otherPages = await Promise.all(pages.map(async page => {
                if (!page?.content) {
                    page = await fetchDoc(page.id);
                }

                return {
                    title: page.name,
                    content: page.content
                }
            }))
        }

        const headPageDoc = (singlePage && pages.find(doc => doc.id === this.currentPage)) || doc;

        const headPage = {
            title: headPageDoc?.name,
            content: headPageDoc?.content
        };

        const html = await getDocPrintHtml([headPage, ...otherPages], withStyles);

        return {
            name: headPage.title,
            html
        };
    }

    trackView() {
        if (this.props.isPublic) {
            trackEvent('DOC.VIEW', {
                via: 'Doc Share URL',
                id: this.props.id,
                name: this.props.doc?.name
            });
        } else {
            trackEvent('DOC.VIEW', {
                via: this.props.inModal ? 'Discussion' : 'Docs',
                id: this.props.id,
                name: this.props.doc?.name
            });
        }
    }

    getCursorElement(userId) {
        const user = (this.props.doc.invitees || []).find(user => user.id === userId);

        if (!user) {
            return null;
        }

        const userCursorCard = (
            <span className="user-cursor-card">
                <span className="card-image">
                    <AvatarBase
                        image={user.image}
                        color={user.color}
                        text={user.initials}
                        size={'xxsmall'}
                    />
                </span>

                <span className="card-body">
                    {user.name.split(' ')[0]} is typing…
                </span>
            </span>
        )

        const userCursorCardHolder = document.createElement('span');

        ReactDOM.render(
            userCursorCard,
            userCursorCardHolder
        );

        return userCursorCardHolder;
    }

    bindCursorElements(cm) {
        const scrollerElement = cm.getScrollerElement();

        this.unbindScrollerHover = delegateHover(scrollerElement, 'other-user-cursor', event => {
            const target = event.target;
            const parentBoundingRect = scrollerElement.getBoundingClientRect();
            const elementBoundingRect = target.getBoundingClientRect();

            if (!target.querySelector('.user-cursor-card')) {
                const userMatch = target.className.match(/other-user-cursor-(.+)\s/);
                if (!userMatch) {
                    return;
                }
                const cursorElement = this.getCursorElement(userMatch[1]);

                if (!cursorElement) {
                    return;
                }

                target.appendChild(cursorElement);
            }

            if (parentBoundingRect.right < elementBoundingRect.right + 200) {
                target.classList.add('close-to-right-edge');
            } else {
                target.classList.remove('close-to-right-edge');
            }
        });
    }

    updateDoc(props) {
        if (!this.props.id) {
            return;
        }

        updateDoc(this.props.id, props);
    }

    closeComponent() {
        stopTrackSession('Document');
    }

    handleCommentsThreadRemove = threadId => {
        this.props.dispatch({
            type: 'MESSAGES_REMOVE_REQUESTED',
            payload: {
                threadId
            }
        });
    }

    handleGoogleDocPermissionChangeClick = () => {
        this.setState({
            googlePermissionsLoading: true
        });

        getAccountToken('google', !this.props.googleAccount.team)
            .then(({ token }) => {
                if (isElectron()) {
                    openWindow(buildAppUrl(`/google-drive-share?token=${token}&doc_id=${this.props.doc.external_id}`), {
                        width: 600,
                        height: 600
                    }, 'drive-share');

                    this.setState({
                        googlePermissionsLoading: false
                    });
                } else {
                    window.gapi && window.gapi.load('drive-share', () => {
                        const s = new window.gapi.drive.share.ShareClient();
                        s.setOAuthToken(token);
                        s.setItemIds([this.props.doc.external_id]);
                        s.showSettingsDialog();

                        this.setState({
                            googlePermissionsLoading: false
                        });
                    });
                }
            });
    }

    handleCollapsedChange = isCollapsed => {
        this.setState({ isCollapsed });
    }

    handleCreatePageClick = async () => {
        const { id, pages } = this.props;


        if (!this.props.doc?.multipage) {
            this.updateDoc({ multipage: true });
        }

        try {
            const doc = await createDoc({
                project: this.props.projectId,
                parent_doc_id: id,
                order: ((pages?.length && pages[pages.length - 1]?.order) || 0) + 100
            });

            this.handlePageSelect(doc.doc_id);
        } catch (e) {
            console.error(e);
        }
    }

    handlePageSelect = pageId => {
        if (this.inModal) {
            this.setState({
                currentPage: pageId
            });
            return;
        }

        const nextPath = pageId === this.props.id ? pageId : `${this.props.id}/${pageId}`;

        if (this.props.isPublic) {
            this.props.history.push(this.props.location.pathname.split('/document/')[0] + `/document/${this.props.projectId}/` + nextPath);
            return;
        }

        this.props.history.push(`/${this.props.projectId}/documents/${this.props.match.params.folderPath || ''}/${nextPath}`);
    }

    get currentUserCanEdit() {
        return this.permission === 'edit';
    }

    get currentUserCanComment() {
        return this.permission === 'comment';
    }

    get readOnly() {
        return this.props.archived || this.props.readOnly || (!this.currentUserCanEdit && this.props.id) || this.props.isPublic || (this.props.doc && this.props.doc.archived);
    }

    get useFullHeader() {
        return !this.props.isPublic || !this.props.projectId;
    }

    get inModal() {
        return this.props.inModal;
    }

    get fromSharedUrl() {
        return !!this.props.isPublic;
    }

    get docActions() {
        if (!this.props.id) {
            return null;
        }

        const { t } = this.props;

        return (
            <div className="header-utils">
                {
                    !this.props.inModal && this.isExternalDoc ? (
                        <div className="util">
                            <ChatToggle />
                        </div>
                    ) : null
                }

                {
                    this.isGoogleDoc ? (
                        <div className="util">
                            {
                                this.currentUserCanEdit && (
                                    this.props.googleAccount ? (
                                        <button className={'btn btn-small btn-secondary btn-border btn-has-loading' + (this.state.googlePermissionsLoading ? ' loading' : '')} disabled={this.state.googlePermissionsLoading} onClick={this.handleGoogleDocPermissionChangeClick}>
                                            <span className="text-condensed">
                                                { t('actions.Edit Doc Permissions') }
                                            </span>
                                        </button>
                                    ) : (
                                        <Link
                                            to={{
                                                pathname: `/settings/apps`,
                                                state: {
                                                    closeUrl: this.props.location.pathname
                                                }
                                            }}
                                        >
                                            <Tooltip tooltip={ t('apps:accounts.google.link') }>
                                                <button className={'btn btn-small btn-secondary btn-border btn-has-loading' + (this.state.googlePermissionsLoading ? ' loading' : '')}>
                                                    <span className="text-condensed">
                                                        { t('actions.Edit Doc Permissions') }
                                                    </span>
                                                </button>
                                            </Tooltip>
                                        </Link>
                                    )
                                )
                            }
                        </div>
                    ) : null
                }

                {
                    this.isOfficeDoc ? (
                        <div className="util">
                            <a className="btn btn-small btn-secondary btn-border" rel="noopener noreferrer" target="_blank" href={`https://onedrive.live.com/edit.aspx?resid=${this.props.doc.external_id}`}>
                                <span className="text-condensed">
                                    { t('actions.Edit in Office365') }
                                </span>
                            </a>
                        </div>
                    ) : null
                }

                <div className="util divided">
                    <DocumentUsers id={this.props.id} size="small" />
                </div>

                <div className="util">
                    <DocActions
                        id={this.props.id}
                        getDocPrintHtml={this.getDocPrintHtml}
                        multipage={this.props.pages?.length > 1}
                        btnClassName="btn btn-simple btn-small btn-square btn-icon"
                        inModal={this.props.inModal}
                        inDoc={true}
                        onArchive={this.props.onArchive}
                    />
                </div>
            </div>
        )
    }

    get permission() {
        if (!this.props.id && !this.props.isPublic) {
            return 'edit';
        }

        if (!this.props.doc || !this.props.doc.members) {
            return null;
        }

        return getDocPermission(this.props.doc.members, this.props.currentUserId);
    }

    get isGoogleDoc() {
        return this.props.doc?.type === 'google';
    }

    get googleType() {
        return this.props.doc?.subtype;
    }

    get isOfficeDoc() {
        return this.props.doc?.type === 'microsoft';
    }

    get isExternalDoc() {
        return this.isGoogleDoc || this.isOfficeDoc;
    }

    get pagesListVisible() {
        return this.props.pages?.length;
    }

    get currentPage() {
        const pageId = !this.inModal ? this.props.match.params.pageId : this.state.currentPage;
        if (pageId === 'head') {
            return null;
        }

        return pageId;
    }

    async attemptFetchPages(force) {
        if (!this.props.id || this.props.pagesLoading || !this.props?.doc?.multipage) {
            return;
        }

        if (!this.props.pagesFetched || force) {
            fetchDocs({ parentDocId: this.props.id, projectId: this.props.projectId });
        }
    }

    setDocCtrl = ctrl => this.docCtrl = ctrl;

    componentDidMount() {
        this.attemptFetchPages(true);

        this.props.provideController && this.props.provideController({
            isPopulated: () => {
                return this.docCtrl && this.docCtrl.isPopulated();
            },
            getContent: () => {
                return this.docCtrl && this.docCtrl.getContent();
            },
            getName: () => {
                return this.docCtrl && this.docCtrl.getName();
            },
            isReadOnly: () => {
                return this.readOnly;
            },
            getEditor: () => {
                return this.docCtrl && this.docCtrl.getEditor();
            }
        });

        if (this.props.doc) {
            this.trackView();
        }

        if (this.props.id) {
            fetchDoc(this.props.id);
        }

        startTrackSession('Document');

        // Tour event
        eventBus.emitEvent('doc:open');

        window.addEventListener('beforeunload', this.handleWindowUnload);
    }

    componentDidUpdate(prevProps) {
        const { t } = this.props;

        this.attemptFetchPages();

        if (this.props.doc && prevProps.doc && !this.props.doc.loading) {
            this.trackView();
        }

        if (!this.props.doc && this.props.id) {
            fetchDoc(this.props.id);
        }
    }

    componentWillUnmount() {
        this.closeComponent();
        this.props.provideController && this.props.provideController(null);
        this.unbindScrollerHover && this.unbindScrollerHover();
        window.removeEventListener('beforeunload', this.handleWindowUnload);

        // Tour event
        eventBus.emitEvent('doc:close');
    }

    render() {
        if (this.props.isPublic && !this.props.doc) {
            return <AppLoading id="doc-loading" timeout={30000} />
        }

        // if (this.props.id && (!this.props.doc || this.props.doc.loading)) {
        //     return <Placeholder />;
        // }

        const { t } = this.props;

        return (
            <DocumentTitle title={`${this.props.doc?.name || t('Untitled Document')} | Nifty`}>
                <div className={'document-editor' + (this.currentUserCanEdit ? ' can-edit' : '') + (this.fromSharedUrl ? ' shared-doc' : '')}>
                    {
                        this.isExternalDoc ? <NotificationsReader notifications={this.props.notifications.map(({ id }) => id)} /> : null
                    }

                    <style>{`[data-comment-key="${this.state.activeCommentsBlock}"], [data-comment-key="${this.state.hoveredCommentsBlock}"] { background-color: #ffede0; }`}</style>

                    {
                        !this.fromSharedUrl ? (
                            <ThemeContext.Consumer>
                                {
                                    ({ theme }) => (
                                        <div className={'header document-editor-header' + (theme === 'light' ? ' header-accent' : '')}>
                                            {
                                                this.inModal ? (
                                                    <div className="header-centered-main">
                                                        {
                                                            this.props.creating ? (
                                                                <React.Fragment key="creating">
                                                                    <h4>{ t('Create Document') }</h4>

                                                                    <p>{ t('docDefaultPermissions') }</p>
                                                                </React.Fragment>
                                                            ) : (
                                                                <React.Fragment key="existing">
                                                                    <h4>{this.state.name}</h4>
                                                                </React.Fragment>
                                                            )
                                                        }
                                                    </div>
                                                ) : (
                                                    <div className="header-main">
                                                        <div className="header-back" onClick={this.handleBackClick}>
                                                            <span className="header-back-arrow">
                                                                <IconArrowsLeft />
                                                            </span>

                                                            {
                                                                this.props.projectId ? (
                                                                    <div className="header-back-avatar">
                                                                        <ProjectAvatar id={this.props.projectId} size="small" />
                                                                    </div>
                                                                ) : null
                                                            }
                                                        </div>

                                                        {
                                                            this.currentUserCanEdit && !this.pagesListVisible ? (
                                                                <button className="btn btn-simple btn-small" onClick={this.handleCreatePageClick}>+ { t('addPageCta') }</button>
                                                            ) : null
                                                        }
                                                    </div>
                                                )
                                            }

                                            {
                                                this.isExternalDoc ? null : (
                                                    <DocToolbar>
                                                        <DocMenu
                                                            activeItems={getToolbarMenuItems(this.permission)}
                                                            items={getToolbarMenuItems('edit')}
                                                            horizontal={true}
                                                            onAction={this.handleMenuAction}
                                                            isCollapsed={this.state.isCollapsed}
                                                            value={this.props?.doc?.content}
                                                            editor={this.docCtrl && this.docCtrl.getEditor()}
                                                        />
                                                    </DocToolbar>
                                                )
                                            }

                                            <div className="header-right">
                                                { this.docActions }
                                            </div>
                                        </div>
                                    )
                                }
                            </ThemeContext.Consumer>
                        ) : null
                    }

                    <div className="document-scroll-proggress">
                        <span style={{ width: `${this.state.scrollPosition}%` }} />
                    </div>

                    <div className="document-editor-main">
                        {
                            this.pagesListVisible ? (
                                <DocPagesList
                                    mainPage={this.props.doc}
                                    pages={this.props.pages}
                                    canEdit={this.currentUserCanEdit}
                                    selectedPage={this.currentPage || this.props.id}
                                    onPageSelect={this.handlePageSelect}
                                    onPageCreate={this.handleCreatePageClick}
                                />
                            ) : null
                        }

                        {
                            this.isGoogleDoc ? (
                                <GoogleDoc docType={this.googleType} googleId={this.props.doc.external_id} />
                            ) : this.isOfficeDoc ? (
                                <OfficeDoc externalId={this.props.doc.external_id} />
                            ) : (
                                <NiftyDoc
                                    key={this.currentPage || this.props.id}
                                    id={this.currentPage || this.props.id}
                                    projectId={this.props.projectId}
                                    canEdit={this.currentUserCanEdit}
                                    permission={this.permission}
                                    noLiveEdit={this.props.noLiveEdit}
                                    docMembers={this.props.docMembers}
                                    preview={this.props.preview}
                                    initialName={this.props.initialName}
                                    isPublic={this.props.isPublic}
                                    initialThreadId={this.props.match.params.threadId}
                                    onScroll={this.handleScroll}
                                    onChange={this.props.onChange}
                                    onNameChange={this.handleNameChange}
                                    onCollapsedChange={this.handleCollapsedChange}
                                    onLoad={this.props.onLoad}
                                    provideController={this.setDocCtrl}
                                />
                            )
                        }

                        {
                            !this.props.inModal && !this.isExternalDoc && !this.props.isPublic ? (
                                <div className="document-chat-toggle">
                                    <ChatToggle />
                                </div>
                            ) : null
                        }
                    </div>
                </div>
            </DocumentTitle>
        );
    }
}

const makeMapStateToProps = () => {
    const getCurrentUserId = makeGetCurrentUserId();
    const getNotificationsByAssociationId = makeGetNotificationsByAssociationId();
    const getDoc = makeGetDoc();
    const getResolvedDocUsers = makeGetResolvedDocUsers();
    const getExternalAccountTeamByProvider = makeGetExternalAccountByProvider();
    const getExternalAccountUserByProvider = makeGetExternalAccountByProvider();
    const getDocPages = makeGetDocPages();
    const getPagesLoading = makeGetLoadingState();
    const getPagesFetched = makeGetFetchedState();

    return ({ projects, docs, users, teams, messages, notifications, externalAccounts }, ownProps) => {
        const routeDocId = ownProps.match.params?.documentId && getCleanIdFromUrl(ownProps.match.params.documentId);

        const docId = ownProps.docId || routeDocId;
        const doc = docId && getDoc(docs.items, docId);
        const projectId = doc?.project || ownProps.projectId || ownProps.match.params?.projectId;

        const teamAccount = getExternalAccountTeamByProvider(externalAccounts.items, 'google', true);
        const userAccount = getExternalAccountUserByProvider(externalAccounts.items, 'google');

        return {
            id: docId,
            projectId,
            currentUserId: getCurrentUserId(users),
            doc,
            pages: doc?.multipage ? getDocPages(docs.items, docId) : null,
            pagesFetched: docId && getPagesFetched(docs.fetchInfo, 'pages', docId),
            pagesLoading: docId && getPagesLoading(docs.fetchInfo, 'pages', docId),
            notifications: getNotificationsByAssociationId(notifications.items, docId),
            docMembers: ownProps.isPublic ? [] : getResolvedDocUsers(doc, users),
            googleAccount: teamAccount || userAccount
        }
    };    
}

export default withRouter(connect(makeMapStateToProps)(withTranslation(['doc', 'date', 'common', 'apps'])(Document)));
